import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import { Box, Button, Card } from '@mui/material';
import { enqueueSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import ConfirmDelete from '../../../../shared/components/ConfirmDelete';
import CustomDataGrid from '../../../../shared/components/CustomDataGrid';
import { ERole } from '../../../../shared/enums/user/ERole';
import { handleError } from '../../../../shared/functions/handleError';
import { useEmployeeColumns } from '../../../../shared/hooks/useEmployeeColumns';
import { usePagination } from '../../../../shared/hooks/usePagination';
import { IUser } from '../../../../shared/interfaces/user/IUser';
import { RootState } from '../../../../store/store';
import { AdminEmployeeService } from '../service';
import EmployeeFilters from './EmployeeFilters';

export const EmployeeTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<IUser[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { userData } = useSelector((state: RootState) => state.auth);
  const isNormalUser = userData?.role === ERole.USER;
  const [selectedUser, setSelectedUser] = useState<undefined | IUser>(
    undefined,
  );
  const columns = useEmployeeColumns(
    setSelectedUser,
    setShowDeleteModal,
    isLoading,
    setIsLoading,
  );
  const {
    pagination,
    count,
    setPagination,
    totalPages,
    setTotalPages,
    setCount,
    handleUpdateList,
    changeFilterHandler,
  } = usePagination({
    perPage: 15,
  });

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedUser(undefined);
  };

  const deleteHandler = async (id: number) => {
    await AdminEmployeeService.delete(id)
      .then(() => {
        enqueueSnackbar(
          t('toasts.successfullyDeleted', { title: selectedUser?.displayName }),
          {
            variant: 'success',
          },
        );
        handleCloseDeleteModal();
        handleUpdateList();
      })
      .catch((err) => handleError(err))
      .finally(() => handleCloseDeleteModal());
  };
  useEffect(() => {
    AdminEmployeeService.getAll({ ...pagination, page: pagination?.page + 1 })
      .then((response) => {
        setCount(response?.count);
        setTotalPages(Math.ceil(response?.count / response?.perPage));
        setData(response.data);
      })
      .catch((err) => handleError(err));
  }, [pagination, setTotalPages, setCount]);

  return (
    <Card>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          paddingBottom: '20px',
        }}
      >
        <EmployeeFilters
          changeFilterHandler={changeFilterHandler}
          pagination={pagination}
        />
        {!isNormalUser && (
          <Button
            sx={{ ml: '10px' }}
            variant={'contained'}
            onClick={() => navigate('/admin/employees/create/1')}
          >
            <AddCircleOutlineOutlinedIcon sx={{ mr: '5px' }} />
            {t('button.addNewUser')}
          </Button>
        )}
      </Box>
      <CustomDataGrid
        hasNextPage={totalPages < pagination?.page}
        rows={data || []}
        columns={columns}
        rowCount={count}
        pagination={pagination}
        setPagination={setPagination}
        loading={data === undefined}
      />

      <ConfirmDelete
        open={showDeleteModal}
        title={selectedUser?.displayName}
        handleClose={handleCloseDeleteModal}
        handleSubmit={() => deleteHandler(Number(selectedUser?.id))}
      />
    </Card>
  );
};
